// src/components/ui/VoiceReader.jsx
// Lector de voz — usa Rime AI (voz "isa", cálida y natural) vía api/rime-tts.
// Si Rime falla o no está configurado (falta RIME_API_KEY), cae de forma
// automática a la voz nativa del navegador (Web Speech API) para que la
// función nunca deje de funcionar.
import React, { useState, useEffect, useRef, useCallback } from 'react';

const MAX_CHUNK_CHARS = 950; // Rime limita a 1000 caracteres por request

const VoiceReader = ({ text, label = 'Escuchar lectura' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const audioRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const objectUrlRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(0);
  const estimatedDurRef = useRef(0);
  const usingFallbackRef = useRef(false);

  useEffect(() => {
    window.speechSynthesis?.getVoices();
    return () => {
      window.speechSynthesis?.cancel();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  // Limpiar markdown/HTML y convertir la estructura visual en pausas reales:
  // Rime no soporta SSML ni tags de pausa — la prosodia depende 100% de la
  // puntuación del texto (coma = pausa breve, punto = pausa plena). Sin este
  // paso, un encabezado markdown queda pegado al párrafo siguiente sin pausa
  // y se escucha corrido/poco expresivo.
  const cleanText = useCallback((raw) => {
    if (!raw) return '';
    return raw
      .replace(/<[^>]*>/g, '')
      // Encabezados (### Título) → frase propia con punto, para forzar una pausa plena antes del párrafo
      .replace(/^#{1,3}\s*(.+)$/gm, (_, title) => {
        const t = title.trim();
        return /[.!?:…]$/.test(t) ? t : `${t}.`;
      })
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/---/g, '')
      .replace(/[✦✨🔮💳🧪⚠️❌✅🎁📓🗑️✏️↕️🌟🤖🔒]/gu, '')
      // Salto de párrafo → pausa plena; salto simple → pausa breve
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, ', ')
      // Limpiar puntuación duplicada que puede quedar de los reemplazos de arriba
      .replace(/([.!?…])\s*\1+/g, '$1')
      .replace(/,\s*\./g, '.')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }, []);

  // Trocea el texto en fragmentos ≤ MAX_CHUNK_CHARS, respetando límites de oración.
  const splitIntoChunks = useCallback((cleaned) => {
    const sentences = cleaned.match(/[^.!?\n]+[.!?\n]*/g) || [cleaned];
    const chunks = [];
    let current = '';
    sentences.forEach((sentence) => {
      if ((current + sentence).length > MAX_CHUNK_CHARS && current) {
        chunks.push(current.trim());
        current = sentence;
      } else {
        current += sentence;
      }
    });
    if (current.trim()) chunks.push(current.trim());
    return chunks;
  }, []);

  // ─── Fallback: voz nativa del navegador (Web Speech API) ────────────
  const getFemaleVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    const spanishVoices = voices.filter((v) => v.lang.startsWith('es'));
    const maleNames = ['pablo', 'jorge', 'andres', 'andrés', 'raul', 'raúl', 'diego',
      'carlos', 'miguel', 'pedro', 'alejandro', 'alvaro', 'álvaro', 'enrique',
      'fernando', 'gonzalo', 'héctor', 'hector', 'ivan', 'iván', 'juan', 'liam',
      'luis', 'manuel', 'marco', 'mario', 'mateo', 'nicolás', 'nicolas', 'oscar',
      'óscar', 'rafael', 'roberto', 'santiago', 'sergio'];
    const femaleNames = ['helena', 'laura', 'sabina', 'elena', 'lucia', 'lucía',
      'maria', 'maría', 'carmen', 'elvira', 'dalia', 'larissa', 'conchita',
      'marina', 'paloma', 'miren', 'abril', 'ximena'];
    const nonMale = spanishVoices.filter((v) => !maleNames.some((m) => v.name.toLowerCase().includes(m)));
    const knownFemale = nonMale.find((v) => femaleNames.some((f) => v.name.toLowerCase().includes(f)));
    if (knownFemale) return knownFemale;
    if (nonMale.length > 0) return nonMale[0];
    return spanishVoices[0] || null;
  }, []);

  const playWithBrowserVoice = useCallback((cleaned) => {
    usingFallbackRef.current = true;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(cleaned);
    const voice = getFemaleVoice();
    if (voice) utter.voice = voice;
    utter.lang = 'es-AR';
    utter.rate = 0.95;
    utter.pitch = 1.05;

    const words = cleaned.split(/\s+/).length;
    estimatedDurRef.current = (words / 2.5) * 1000;

    utter.onstart = () => {
      setIsPlaying(true);
      setIsLoadingAudio(false);
      startTimeRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setProgress(Math.min((elapsed / estimatedDurRef.current) * 100, 95));
      }, 200);
    };
    utter.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimeout(() => setProgress(0), 1200);
    };
    utter.onerror = (e) => {
      if (e.error !== 'canceled') setError('Error al reproducir la voz');
      setIsPlaying(false);
      setIsPaused(false);
      setIsLoadingAudio(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    window.speechSynthesis.speak(utter);
  }, [getFemaleVoice]);

  // ─── Voz de Rime AI (server-side) con fragmentos encadenados ────────
  const fetchChunkAudio = useCallback(async (chunkText) => {
    const response = await fetch('/api/rime-tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: chunkText }),
    });
    if (!response.ok) throw new Error('rime-tts-failed');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }, []);

  const playChunk = useCallback(async (index) => {
    const chunks = chunksRef.current;
    if (index >= chunks.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      setTimeout(() => setProgress(0), 1200);
      return;
    }

    // Único punto de fallback: lo llaman tanto el catch (falla al iniciar el
    // chunk) como audio.onerror (falla a mitad de la reproducción) — antes
    // onerror hacía "throw" dentro de un callback async, lo cual NUNCA llega
    // al catch de más abajo y dejaba la lectura cortada en silencio.
    const fallbackFromHere = (err) => {
      console.error('Error reproduciendo audio de Rime:', err);
      setIsLoadingAudio(false);
      const remaining = chunksRef.current.slice(index).join(' ');
      playWithBrowserVoice(remaining || chunksRef.current.join(' '));
    };

    try {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = await fetchChunkAudio(chunks[index]);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onplay = () => { setIsPlaying(true); setIsLoadingAudio(false); };
      audio.ontimeupdate = () => {
        const chunkProgress = audio.duration ? audio.currentTime / audio.duration : 0;
        const overall = ((index + chunkProgress) / chunks.length) * 100;
        setProgress(Math.min(overall, 99));
      };
      audio.onended = () => {
        chunkIndexRef.current = index + 1;
        playChunk(index + 1);
      };
      audio.onerror = () => fallbackFromHere(new Error('audio-playback-failed'));

      await audio.play();
    } catch (err) {
      fallbackFromHere(err);
    }
  }, [fetchChunkAudio, playWithBrowserVoice]);

  const handlePlay = useCallback(async () => {
    if (!text) return;

    if (isPaused) {
      if (usingFallbackRef.current) {
        window.speechSynthesis.resume();
      } else if (audioRef.current) {
        audioRef.current.play();
      }
      setIsPaused(false);
      setIsPlaying(true);
      return;
    }

    const cleaned = cleanText(text);
    if (!cleaned) return;

    setError('');
    usingFallbackRef.current = false;

    if (!window.RIME_TTS_DISABLED) {
      setIsLoadingAudio(true);
      chunksRef.current = splitIntoChunks(cleaned);
      chunkIndexRef.current = 0;
      playChunk(0).catch(() => {
        window.RIME_TTS_DISABLED = true; // evita reintentar Rime el resto de la sesión si ya falló
        playWithBrowserVoice(cleaned);
      });
    } else {
      playWithBrowserVoice(cleaned);
    }
  }, [text, isPaused, cleanText, splitIntoChunks, playChunk, playWithBrowserVoice]);

  const handlePause = useCallback(() => {
    if (usingFallbackRef.current) {
      window.speechSynthesis.pause();
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPaused(true);
    setIsPlaying(false);
  }, []);

  const handleStop = useCallback(() => {
    window.speechSynthesis?.cancel();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoadingAudio(false);
    setProgress(0);
    setError('');
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  if (!text) return null;

  return (
    <div className="voice-reader">
      <div className="voice-reader-controls">
        {!isPlaying && !isPaused && (
          <button onClick={handlePlay} className="voice-btn voice-btn--play" title={label} disabled={isLoadingAudio}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            <span>{isLoadingAudio ? 'Cargando voz...' : label}</span>
          </button>
        )}

        {isPlaying && (
          <>
            <button onClick={handlePause} className="voice-btn voice-btn--pause" title="Pausar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              <span>Pausar</span>
            </button>
            <button onClick={handleStop} className="voice-btn voice-btn--stop" title="Detener">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 6h12v12H6z" />
              </svg>
            </button>
          </>
        )}

        {isPaused && !isPlaying && (
          <>
            <button onClick={handlePlay} className="voice-btn voice-btn--play" title="Continuar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              <span>Continuar</span>
            </button>
            <button onClick={handleStop} className="voice-btn voice-btn--stop" title="Detener">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 6h12v12H6z" />
              </svg>
            </button>
          </>
        )}
      </div>

      {(isPlaying || isPaused) && (
        <div className="voice-progress-bar">
          <div className="voice-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      {error && (
        <p style={{ color: '#f87171', fontSize: '0.8rem', marginTop: '0.3rem' }}>{error}</p>
      )}
    </div>
  );
};

export default VoiceReader;
